import { type PlanState, useTranslation } from "@next-platform/client-common-core";
import { Icon, IconSize, Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import type { UserRole } from "@next-platform/packages-ts-types";
import { Progress } from "@nextui-org/react";
import type { TFunction } from "i18next";

import { Col, Row } from "@/components/customComponents";
import Trans from "@/components/Trans/Trans";
import { getPlanDisplayColors, isExpiredPlanState, resolveStatusTextColor } from "@/constants/planTierColors";
import { cn } from "@/utils";
import { PLAN_STATE_STATUS, resolvePeriodLabel, resolveStatusLabel } from "@/utils/planStateLabels";

type HeroContentParams = {
  formattedExpiryDate: string;
  hasExpiryDate: boolean;
  daysRemaining: number;
};

type GetHeroContentParams = {
  planState: PlanState;
  t: TFunction;
  contentParams: HeroContentParams;
};

type HeroContent = {
  statusLabel: string;
  periodLabel: string;
  progressAriaLabel: string;
};

const getHeroContent = ({ planState, t, contentParams }: GetHeroContentParams): HeroContent => {
  const { formattedExpiryDate, hasExpiryDate, daysRemaining } = contentParams;

  return {
    statusLabel: resolveStatusLabel({ planState, t }),
    periodLabel: resolvePeriodLabel({
      planState,
      hasExpiryDate,
      formattedExpiryDate,
      daysRemaining,
      t,
      includeDaysCount: false,
    }),
    progressAriaLabel: t("subscription:overview.progressLabel"),
  };
};

type SubscriptionPlanHeroProps = {
  planRole: UserRole;
  planState: PlanState;
  planName: string;
  daysRemaining: number;
  progressValue: number;
  formattedExpiryDate: string;
  hasExpiryDate: boolean;
};

const SubscriptionPlanHero = ({
  planRole,
  planState,
  planName,
  daysRemaining,
  progressValue,
  formattedExpiryDate,
  hasExpiryDate,
}: SubscriptionPlanHeroProps) => {
  const { t } = useTranslation();
  const colors = getPlanDisplayColors({ role: planRole, planState });
  const statusIcon = PLAN_STATE_STATUS[planState];
  const statusTextColor = resolveStatusTextColor(colors);
  const isExpiredPlan = isExpiredPlanState(planState);
  const content = getHeroContent({
    planState,
    t,
    contentParams: { formattedExpiryDate, hasExpiryDate, daysRemaining },
  });
  return (
    <Col className={cn("p-6 gap-5 bg-gradient-to-r", colors.heroFrom, colors.heroVia, "to-transparent")} data-testid="SubscriptionPlan-Hero">
      <Row align="center" justify="between" className="gap-md">
        <div
          className={cn(
            "inline-flex items-center gap-2 rounded-md px-3 py-1.5",
            isExpiredPlan ? cn("bg-white/10 border border-solid border-gray-8") : cn("bg-gradient-to-b", colors.iconBadgeFrom, colors.iconBadgeTo),
          )}
          data-testid="SubscriptionPlan-PlanBadge"
        >
          <Text variant={TextVariants.LABEL_MEDIUM_MEDIUM} className="text-white">
            {planName}
          </Text>
          <Icon icon={colors.icon} size={IconSize.SMALL} className="text-white" />
        </div>
        {statusIcon ? (
          <div className={cn("inline-flex items-center gap-1.5 rounded-full border border-solid px-2.5 py-1", colors.border, colors.background)}>
            <Icon icon={statusIcon} size={IconSize.XSMALL} className={statusTextColor} />
            <Text variant={TextVariants.LABEL_SMALL_REGULAR} className={statusTextColor}>
              {content.statusLabel}
            </Text>
          </div>
        ) : null}
      </Row>

      <Col className="gap-3">
        <Row align="baseline" className="gap-2">
          <Text variant={TextVariants.DISPLAY_SMALL_BOLD} className="text-gray-12">
            {daysRemaining}
          </Text>
          <Text variant={TextVariants.PARAGRAPH_MEDIUM_MEDIUM} className="text-gray-12">
            <Trans i18nKey="subscription:overview.daysRemainingLabel" />
          </Text>
        </Row>
        <Progress
          aria-label={content.progressAriaLabel}
          value={progressValue}
          classNames={{
            base: "h-2 w-full",
            track: "bg-gray-8 dark:bg-gray-7",
            indicator: colors.bar,
          }}
        />
        {content.periodLabel.length > 0 ? (
          <Text variant={TextVariants.PARAGRAPH_SMALL_REGULAR} className="text-gray-11">
            {content.periodLabel}
          </Text>
        ) : null}
      </Col>
    </Col>
  );
};

export default SubscriptionPlanHero;
